// ============================================================
// frontend/src/views/ChangePasswordView.tsx
// ============================================================
// Forced-password-change screen. Shown when the logged-in therapist
// has `mustChangePassword=true` (admin created their account with a
// temp password). The user can't reach any other view until they
// finish this flow. Refreshes the auth state on success so the rest
// of the app unlocks.
// ============================================================

import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSaving } from '../lib/useSaving';
import { ConsiliumMark } from '../components/Brand';

export function ChangePasswordView() {
  const { therapist, refresh, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const { saving, saved, run } = useSaving();

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMsg('');
    if (newPassword !== confirmPassword) {
      setErrorMsg('New password and confirmation do not match.');
      return;
    }
    try {
      await run(async () => {
        await api.post('/auth/change-password', { currentPassword, newPassword });
        // Re-fetch the session so mustChangePassword flips to false and the
        // app reveals the rest of the views.
        await refresh();
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to change password');
    }
  };

  const disabled = !currentPassword || !newPassword || !confirmPassword;

  return (
    <div className="login-shell">
      <div className="login-orb login-orb-1" aria-hidden="true" />
      <div className="login-orb login-orb-2" aria-hidden="true" />
      <div className="login-orb login-orb-3" aria-hidden="true" />

      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-mark">
            <ConsiliumMark size={56} animate ariaLabel="Consilium" />
          </div>
          <div className="login-brand-name" aria-hidden="true">Consilium</div>
          <div className="login-rule" aria-hidden="true" />
          <div className="login-tagline">
            Welcome{therapist?.name ? `, ${therapist.name.split(' ')[0]}` : ''}.
            Set a new password to continue.
          </div>
        </div>

        <form onSubmit={onSubmit} className="stack-lg login-form">
          <div className="float-field">
            <input
              id="cp-current"
              type="password"
              className="input"
              placeholder=" "
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
            <label className="float-field-label" htmlFor="cp-current">Temporary password</label>
          </div>

          <div className="float-field">
            <input
              id="cp-new"
              type="password"
              className="input"
              placeholder=" "
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <label className="float-field-label" htmlFor="cp-new">New password (8+ characters)</label>
          </div>

          <div className="float-field">
            <input
              id="cp-confirm"
              type="password"
              className="input"
              placeholder=" "
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <label className="float-field-label" htmlFor="cp-confirm">Confirm new password</label>
          </div>

          {errorMsg && <div className="login-alert is-error">{errorMsg}</div>}

          <button
            type="submit"
            className={`btn-premium${saved ? ' is-saved' : ''}`}
            disabled={disabled || saving}
          >
            {saving && <span className="spin" aria-hidden="true" />}
            {saving ? 'Updating…' : saved ? 'Updated' : 'Set new password'}
          </button>
        </form>

        <div className="login-foot">
          Wrong account?{' '}
          <button
            type="button"
            onClick={() => logout()}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'var(--accent)', cursor: 'pointer', font: 'inherit',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
